<template>
  <GenerativeFlux
    :effectCode="effectCode"
    :effectType="effectType"
    :effectConfig="currentEffectConfig"
  />
</template>


<script setup>
import { useRoute } from "vue-router";
import GenerativeFlux from "@/components/ai-effects/GenerativeFlux.vue";
import { resolveEffectMediaFields } from "@/composables/useAiR2ModelsAndEffects";

definePageMeta({ layout: "default-new" });

const route = useRoute();
const pathParts = computed(() => {
  const slugParams = route.params.slug;
  const targetPath = Array.isArray(slugParams)
    ? "/" + slugParams.join("/")
    : "";
  return targetPath.replace(/^\/|\/$/g, "").split("/");
});
const effectType = computed(() => pathParts.value[0] || "");
const effectCode = computed(() => pathParts.value[1] || "");
const effectConfigKey = computed(
  () => `ai-effect-config-${effectType.value}-${effectCode.value}`
);
const r2CachedFetch = useR2CachedFetch();

function isValidEffectType(type) {
  return type === "image" || type === "video";
}

if (!isValidEffectType(effectType.value) || !effectCode.value) {
  throw createError({ statusCode: 404, statusMessage: "Effect not found" });
}

const { data: effectConfigResult } = await useAsyncData(
  effectConfigKey,
  async () => {
    const key = effectConfigKey.value;
    try {
      const config = await r2CachedFetch(
        `/api/r2/ai/effects/${effectType.value}/configs/${effectCode.value}.json`
      );
      return { key, config: resolveEffectMediaFields(config), failed: false };
    } catch {
      return { key, config: null, failed: true };
    }
  },
  {
    watch: [() => effectType.value, () => effectCode.value],
    default: () => ({ key: "", config: null, failed: false }),
    lazy: import.meta.client,
  }
);

const isLoadedCurrentEffectConfig = computed(() => {
  return effectConfigResult.value?.key === effectConfigKey.value;
});

const isCurrentEffectConfig = computed(() => {
  const config = effectConfigResult.value?.config;
  return (
    isLoadedCurrentEffectConfig.value &&
    config?.effect_type === effectType.value &&
    config?.code === effectCode.value
  );
});

const currentEffectConfig = computed(() => {
  return isCurrentEffectConfig.value ? effectConfigResult.value.config : null;
});

const shouldShowEffectNotFound = computed(() => {
  return Boolean(
    isLoadedCurrentEffectConfig.value &&
      (effectConfigResult.value?.failed || !isCurrentEffectConfig.value)
  );
});

if (import.meta.server && shouldShowEffectNotFound.value) {
  throw createError({ statusCode: 404, statusMessage: "Effect not found" });
}

watchEffect(() => {
  if (!import.meta.client || !shouldShowEffectNotFound.value) return;
  showError(createError({ statusCode: 404, statusMessage: "Effect not found" }));
});
</script>
